export interface PlantSaveData {
  id: string;
  species: string;
  position: { x: number; y: number };
  currentStage: string;
  stageProgress: number;
  state: string;
  health: number;
  moisture: number;
  supplementalLight: number;
  wateringAmount: number;
  temperature: number;
  history: Array<{
    timestamp: number;
    event: string;
    detail?: any;
  }>;
}

export interface DetachedPanelState {
  tab: string;
  left: number;
  top: number;
}

export interface SaveData {
  version: string;
  createdAt: number;
  lastPlayedAt: number;
  totalPlayTime: number;

  plants: PlantSaveData[];
  currency: number;
  detachedPanels?: DetachedPanelState[];

  settings: {
    timeSpeed: number;
  };
}

export interface GardenMeta {
  id: string;
  name: string;
  createdAt: number;
  lastPlayedAt: number;
}

export interface Garden extends GardenMeta {
  saveData: SaveData;
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export class SaveManager {
  private static GARDENS_KEY = 'plotlet_gardens_v2';
  private static CURRENT_KEY = 'plotlet_garden_current_v2';

  static loadAllGardens(): Garden[] {
    try {
      const json = localStorage.getItem(this.GARDENS_KEY);
      if (!json) return [];
      return JSON.parse(json) as Garden[];
    } catch {
      return [];
    }
  }

  static loadGarden(id: string): Garden | null {
    const gardens = this.loadAllGardens();
    return gardens.find(g => g.id === id) || null;
  }

  private static saveAllGardens(gardens: Garden[]): void {
    try {
      localStorage.setItem(this.GARDENS_KEY, JSON.stringify(gardens));
    } catch (e) {
      console.error('Failed to save gardens:', e);
    }
  }

  static save(data: SaveData): void {
    const id = this.getCurrentId();
    if (!id) return;
    const gardens = this.loadAllGardens();
    const idx = gardens.findIndex(g => g.id === id);
    if (idx >= 0) {
      data.createdAt = gardens[idx].createdAt;
      gardens[idx].saveData = data;
      gardens[idx].lastPlayedAt = data.lastPlayedAt;
    } else {
      const name = '我的花园';
      gardens.push({
        id,
        name,
        createdAt: data.createdAt,
        lastPlayedAt: data.lastPlayedAt,
        saveData: data,
      });
    }
    this.saveAllGardens(gardens);
  }

  static load(): SaveData | null {
    const id = this.getCurrentId();
    if (!id) return null;
    const garden = this.loadGarden(id);
    return garden ? garden.saveData : null;
  }

  static createGarden(name: string, data: SaveData): Garden {
    const garden: Garden = {
      id: generateId(),
      name,
      createdAt: data.createdAt,
      lastPlayedAt: data.lastPlayedAt,
      saveData: data,
    };
    const gardens = this.loadAllGardens();
    gardens.push(garden);
    this.saveAllGardens(gardens);
    this.setCurrentId(garden.id);
    return garden;
  }

  static deleteGarden(id: string): void {
    let gardens = this.loadAllGardens();
    gardens = gardens.filter(g => g.id !== id);
    this.saveAllGardens(gardens);

    if (this.getCurrentId() === id) {
      if (gardens.length > 0) {
        this.setCurrentId(gardens[0].id);
      } else {
        this.setCurrentId(null);
      }
    }
  }

  static getCurrentId(): string | null {
    return localStorage.getItem(this.CURRENT_KEY) || null;
  }

  static setCurrentId(id: string | null): void {
    if (id) {
      localStorage.setItem(this.CURRENT_KEY, id);
    } else {
      localStorage.removeItem(this.CURRENT_KEY);
    }
  }

  static exportToFile(data: SaveData): void {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plotlet-save-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  static async importFromFile(file: File): Promise<SaveData | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string) as SaveData;
          if (data.version && data.plants) {
            resolve(data);
          } else {
            resolve(null);
          }
        } catch {
          resolve(null);
        }
      };
      reader.onerror = () => resolve(null);
      reader.readAsText(file);
    });
  }

  static clear(): void {
    localStorage.removeItem(this.GARDENS_KEY);
    localStorage.removeItem(this.CURRENT_KEY);
  }
}
